'use client';

import { useFormContext, type RegisterOptions } from 'react-hook-form';

import { cn } from '@/utils/cn';
import { getErrorMessage } from '@/utils/formErrors';
import Text from '@/components/ui/Text';

const FormTextField = ({
  name,
  label,
  placeholder,
  required = false,
  inputType,
  className,
  messages,
  validationType = 'default',
}: FormTextFieldProps) => {
  const {
    register,
    formState: { errors },
  } = useFormContext<Record<string, string>>();

  const error = getErrorMessage(errors, name);

  const rules: RegisterOptions<Record<string, string>, string> = {};

  if (required) rules.required = messages.required;

  switch (validationType) {
    case 'email':
      rules.pattern = { value: EMAIL_PATTERN, message: messages.invalidEmail };
      break;
    case 'phone':
      rules.pattern = { value: PHONE_PATTERN, message: messages.invalidPhone };
      break;
    case 'name':
      rules.pattern = { value: NAME_PATTERN, message: messages.invalidName };
      break;
  }

  const fieldClassName = cn(
    'w-full rounded-xs border border-primary px-4 py-3 text-text outline-none transition-colors',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:border-primary',
    error && 'border-secondary focus:border-secondary',
  );

  const isTextarea = inputType === 'textarea';

  return (
    <div className={cn('w-full', className)}>
      <label htmlFor={name} className="flex items-center gap-1">
        <Text as="p" variant="formLabel">
          {label}
        </Text>
        {required && (
          <span aria-hidden="true" className="text-secondary">
            *
          </span>
        )}
      </label>

      {isTextarea ? (
        <textarea
          id={name}
          rows={6}
          placeholder={placeholder}
          className={fieldClassName}
          aria-invalid={Boolean(error)}
          aria-describedby={`${name}-error`}
          aria-required={required}
          {...register(name, rules)}
        />
      ) : (
        <input
          id={name}
          type={inputType}
          placeholder={placeholder}
          className={fieldClassName}
          aria-invalid={Boolean(error)}
          aria-describedby={`${name}-error`}
          aria-required={required}
          {...register(name, rules)}
        />
      )}

      <Text
        as="p"
        role="alert"
        aria-live="polite"
        variant="formError"
        id={`${name}-error`}
        className={cn(
          'mt-1 min-h-[1rem] transition-opacity duration-200',
          error ? 'opacity-100' : 'opacity-0',
        )}
      >
        {error ?? ' '}
      </Text>
    </div>
  );
};

export default FormTextField;

type Messages = {
  required: string;
  invalidEmail: string;
  invalidPhone: string;
  invalidName: string;
};

type ValidationType = 'default' | 'email' | 'phone' | 'name';

type FormTextFieldProps = {
  name: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  inputType: 'text' | 'email' | 'tel' | 'textarea';
  className?: string;
  messages: Messages;
  validationType?: ValidationType;
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_PATTERN = /^\+?[0-9\s]{9,15}$/;
const NAME_PATTERN = /^[A-Za-zÀ-ÖØ-öø-ÿ]{2,}(?:\s+[A-Za-zÀ-ÖØ-öø-ÿ]{2,})+$/;
